// ⚠️ GENERATED COPY — DO NOT EDIT.
// Source: shared/ui/journey/GapCarousel.tsx — edit there, then run: bash scripts/sync_shared_code.sh
// CI verifies these copies match the source (sync_shared_code.sh --check).
import React, { useEffect, useRef, useState } from "react";
import { J, ensureJourneyKeyframes, motionOk } from "./journeyTheme";
import {
  answeredCount,
  buildSteps,
  clampIndex,
  progressPercent,
  stepDotStates,
  stepHeading,
  stepNextLabel,
  type GapAnswers,
  type GapGroup,
  type GapQuestion,
} from "../../lib/gapCarousel";

/**
 * The facts we still need, one card at a time (or all at once in list mode).
 * Given `groups`, a card holds a whole topic (name, sex, birth date…) rather
 * than a single question; a one-question group renders as it always has.
 * Ported from the approved plan-holder prototype (Main.dc.html, questions
 * screen). The page supplies the heading; this owns the counter, the card,
 * the dots and the footer. Answers are controlled by the caller.
 */

export interface GapCarouselProps {
  questions: GapQuestion[];
  answers: GapAnswers;
  onAnswer: (key: string, value: string) => void;
  onComplete: () => void;
  onBack?: () => void;
  mode?: "carousel" | "list";
  continueLabel?: string;
  /** Carousel only: one card per group instead of one per question. */
  groups?: GapGroup[];
}

// The question card sits a little higher than the fact cards in the
// prototype, so it carries its own deeper shadow and radius.
const RAISED_SHADOW = "0 1px 2px rgba(24,42,51,.06), 0 8px 22px rgba(24,42,51,.07)";
const FADE_MS = 220;
const PICK_ADVANCE_MS = 260;

// React 17 has no useId; a module counter gives each mount stable label ids.
let idSeq = 0;

const cardStyle: React.CSSProperties = {
  background: J.white,
  borderRadius: 18,
  padding: "20px 18px",
  boxShadow: RAISED_SHADOW,
  display: "flex",
  flexDirection: "column",
  gap: 14,
};

const stemStyle: React.CSSProperties = {
  fontSize: 19,
  fontWeight: 700,
  lineHeight: 1.3,
  color: J.ink,
};

const helpStyle: React.CSSProperties = {
  fontSize: 13.5,
  color: J.muted,
  marginTop: -8,
};

// Inside a group card the group title is the heading, so each question's own
// stem steps down to a field label.
const compactStemStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  lineHeight: 1.35,
  color: J.ink,
};

const compactHelpStyle: React.CSSProperties = { ...helpStyle, marginTop: -4 };

function inputStyle(focused: boolean): React.CSSProperties {
  return {
    height: 48,
    border: `1.5px solid ${focused ? J.teal : J.border}`,
    borderRadius: 12,
    padding: "0 14px",
    fontSize: 16,
    fontFamily: "inherit",
    color: J.ink,
    background: J.white,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };
}

function choiceStyle(selected: boolean): React.CSSProperties {
  return {
    flex: "1 1 0",
    minWidth: 0,
    height: 48,
    borderRadius: 12,
    border: `1.5px solid ${selected ? J.ink : J.border}`,
    background: selected ? J.ink : J.white,
    color: selected ? J.white : J.ink,
    fontSize: 16,
    fontWeight: 600,
    fontFamily: "inherit",
    cursor: "pointer",
    transition: "background .18s ease, border-color .18s ease",
  };
}

function htmlInputType(t: GapQuestion["type"]): string {
  // "number" stays a text box with a numeric keypad: type=number swallows
  // partial entries and spins on scroll, which loses answers on phones.
  if (t === "number") return "text";
  return t === "choice" ? "text" : t;
}

function inputModeFor(t: GapQuestion["type"]): React.HTMLAttributes<HTMLInputElement>["inputMode"] {
  if (t === "number") return "decimal";
  if (t === "tel") return "tel";
  if (t === "email") return "email";
  return undefined;
}

const ArrowRight = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

interface FieldProps {
  q: GapQuestion;
  id: string;
  value: string;
  onAnswer: (key: string, value: string) => void;
  onPick?: () => void;
  onEnter?: (input: HTMLInputElement) => void;
  compact?: boolean;
}

function QuestionBody({ q, id, value, onAnswer, onPick, onEnter, compact }: FieldProps) {
  const [focused, setFocused] = useState(false);
  const stemId = `${id}-stem`;
  const stemCss = compact ? compactStemStyle : stemStyle;
  const helpCss = compact ? compactHelpStyle : helpStyle;

  if (q.type === "choice") {
    return (
      <>
        <div id={stemId} style={stemCss}>
          {q.stem}
        </div>
        {q.help ? <div style={helpCss}>{q.help}</div> : null}
        <div role="group" aria-labelledby={stemId} style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {(q.options || []).map((opt) => {
            const selected = value === opt;
            return (
              <button
                key={opt}
                type="button"
                aria-pressed={selected}
                style={choiceStyle(selected)}
                onClick={() => {
                  onAnswer(q.key, opt);
                  if (onPick) onPick();
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </>
    );
  }

  return (
    <>
      <label id={stemId} htmlFor={id} style={stemCss}>
        {q.stem}
      </label>
      {q.help ? <div style={helpCss}>{q.help}</div> : null}
      <input
        id={id}
        type={htmlInputType(q.type)}
        inputMode={inputModeFor(q.type)}
        placeholder={q.placeholder || "Type your answer"}
        value={value}
        onChange={(e) => onAnswer(q.key, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && onEnter) {
            // Inside a page <form> Enter would submit the whole page.
            e.preventDefault();
            onEnter(e.currentTarget);
          }
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={inputStyle(focused)}
      />
    </>
  );
}

export default function GapCarousel({
  questions,
  answers,
  onAnswer,
  onComplete,
  onBack,
  mode = "carousel",
  continueLabel,
  groups,
}: GapCarouselProps) {
  const idRef = useRef<string>("");
  if (!idRef.current) idRef.current = `pj-gap-${++idSeq}`;
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(1);
  const [slide, setSlide] = useState(0);
  const timers = useRef<number[]>([]);
  // Blocks a second advance while the card is mid-swap (double tap, or a
  // pick's auto-advance racing a Next press).
  const busy = useRef(false);
  const completedEmpty = useRef(false);
  const pickTimer = useRef<number | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  // Latest callbacks for timers that fire after a re-render.
  const latest = useRef({ onComplete, onBack });
  latest.current = { onComplete, onBack };

  const total = questions.length;

  useEffect(() => {
    ensureJourneyKeyframes();
    const pending = timers.current;
    return () => {
      pending.forEach((t) => window.clearTimeout(t));
      pending.length = 0;
    };
  }, []);

  useEffect(() => {
    if (total === 0 && !completedEmpty.current) {
      completedEmpty.current = true;
      latest.current.onComplete();
    }
  }, [total]);

  if (total === 0) return null;

  const later = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  };

  const steps = buildSteps(questions, mode === "carousel" ? groups : null);
  const stepCount = steps.length;
  const si = clampIndex(index, stepCount);
  const step = steps[si];
  const isLast = si === stepCount - 1;
  const { heading, grouped } = stepHeading(step);

  function advance(delta: number) {
    // A manual press supersedes a pick's pending auto-advance, so one tap
    // plus Next never moves two questions or completes twice.
    if (pickTimer.current !== null) {
      window.clearTimeout(pickTimer.current);
      pickTimer.current = null;
    }
    if (busy.current) return;
    const next = si + delta;
    if (next >= stepCount) {
      latest.current.onComplete();
      return;
    }
    if (next < 0) {
      if (latest.current.onBack) latest.current.onBack();
      return;
    }
    if (!motionOk()) {
      setIndex(next);
      return;
    }
    busy.current = true;
    setFade(0);
    setSlide(delta > 0 ? -14 : 14);
    later(() => {
      setIndex(next);
      setFade(1);
      setSlide(0);
      busy.current = false;
    }, FADE_MS);
  }

  function pickThenAdvance() {
    // The pause lets the selected fill register before the card moves on.
    if (pickTimer.current !== null) window.clearTimeout(pickTimer.current);
    pickTimer.current = window.setTimeout(() => {
      pickTimer.current = null;
      advance(1);
    }, PICK_ADVANCE_MS);
    timers.current.push(pickTimer.current);
  }

  const footerButton: React.CSSProperties = {
    height: 48,
    borderRadius: J.radiusButton,
    fontSize: 15,
    fontWeight: 600,
    fontFamily: "inherit",
    cursor: "pointer",
  };

  function enterFrom(input: HTMLInputElement) {
    // Enter moves down the card's boxes and only advances from the last one,
    // so a group is not skipped half-filled.
    const inputs = Array.from(cardRef.current ? cardRef.current.querySelectorAll("input") : []);
    const at = inputs.indexOf(input);
    if (at >= 0 && at < inputs.length - 1) inputs[at + 1].focus();
    else advance(1);
  }

  if (mode === "list") {
    const animate = motionOk();
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {questions.map((lq, i) => (
          <div
            key={lq.key}
            style={{
              ...cardStyle,
              animation: animate ? `pj-fadeUp .45s ease ${Math.min(i, 6) * 90}ms both` : undefined,
            }}
          >
            <QuestionBody
              q={lq}
              id={`${idRef.current}-${i}`}
              value={answers[lq.key] || ""}
              onAnswer={onAnswer}
            />
          </div>
        ))}
        <button
          type="button"
          onClick={() => onComplete()}
          style={{
            marginTop: 2,
            width: "100%",
            height: 52,
            border: 0,
            borderRadius: J.radiusButton,
            background: J.ink,
            color: J.white,
            fontSize: 16,
            fontWeight: 600,
            fontFamily: "inherit",
            cursor: "pointer",
          }}
        >
          {continueLabel ?? "Continue"}
        </button>
      </div>
    );
  }

  const backDisabled = si === 0 && !onBack;
  const pct = progressPercent(si, stepCount);
  const label = isLast && continueLabel ? continueLabel : stepNextLabel(step, answers, isLast);
  const dotColor = { current: J.ink, answered: J.sage, empty: J.border };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, flex: "1 1 0" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div
          aria-live="polite"
          style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: J.muted }}
        >
          <span>
            {groups ? "Step" : "Question"} {si + 1} of {stepCount}
          </span>
          <span>{answeredCount(questions, answers)} answered</span>
        </div>
        <div
          role="progressbar"
          aria-label="Questions progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          style={{ height: 5, borderRadius: 3, background: J.line, overflow: "hidden" }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: J.teal,
              borderRadius: 3,
              transition: "width .35s ease",
            }}
          />
        </div>
      </div>

      <div
        ref={cardRef}
        role={grouped ? "group" : undefined}
        aria-labelledby={grouped ? `${idRef.current}-heading` : undefined}
        style={{
          ...cardStyle,
          opacity: fade,
          transform: `translateX(${slide}px)`,
          transition: "opacity .22s ease, transform .22s ease",
        }}
      >
        {grouped ? (
          <>
            <div id={`${idRef.current}-heading`} style={stemStyle}>
              {heading}
            </div>
            {step.questions.map((gq, i) => (
              <div key={gq.key} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {/* No onPick: a tap fills one box of several, so the card
                    waits for Next rather than jumping away. */}
                <QuestionBody
                  compact
                  q={gq}
                  id={`${idRef.current}-${step.key}-${i}`}
                  value={answers[gq.key] || ""}
                  onAnswer={onAnswer}
                  onEnter={enterFrom}
                />
              </div>
            ))}
          </>
        ) : (
          <QuestionBody
            key={step.questions[0].key}
            q={step.questions[0]}
            id={idRef.current}
            value={answers[step.questions[0].key] || ""}
            onAnswer={onAnswer}
            onPick={pickThenAdvance}
            onEnter={() => advance(1)}
          />
        )}
      </div>

      <div aria-hidden="true" style={{ display: "flex", justifyContent: "center", gap: 6 }}>
        {stepDotStates(steps, si, answers).map((d, i) => (
          <div
            key={steps[i].key}
            style={{
              width: d.width,
              height: 6,
              borderRadius: 3,
              background: dotColor[d.color],
              transition: "width .25s ease, background .25s ease",
            }}
          />
        ))}
      </div>

      <div style={{ marginTop: "auto", display: "flex", gap: 10, alignItems: "center", paddingTop: 10 }}>
        <button
          type="button"
          onClick={() => advance(-1)}
          disabled={backDisabled}
          style={{
            ...footerButton,
            padding: "0 16px",
            border: `1.5px solid ${J.border}`,
            background: J.white,
            color: J.ink,
            opacity: backDisabled ? 0.5 : 1,
            cursor: backDisabled ? "default" : "pointer",
          }}
        >
          Back
        </button>
        {/* The quiet Skip only when the primary button says something else;
            with nothing answered the primary already reads "Skip", and two
            Skips side by side read as a mistake (Michael's walk, 2026-10-09). */}
        {label !== "Skip" ? (
          <button
            type="button"
            onClick={() => advance(1)}
            style={{ ...footerButton, padding: "0 12px", border: 0, background: "transparent", color: J.muted }}
          >
            Skip
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => advance(1)}
          style={{
            ...footerButton,
            marginLeft: "auto",
            padding: "0 22px",
            border: 0,
            background: J.ink,
            color: J.white,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {label} <ArrowRight />
        </button>
      </div>
    </div>
  );
}
